import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../utils/api';
import './PsychotestManager.css';

const DIMENSIONS = [
  { value: 'extro_intro', label: 'Ekstroversi vs Introversi' },
  { value: 'emotional_stability', label: 'Stabilitas Emosi' },
  { value: 'openness_innovation', label: 'Keterbukaan & Inovasi' },
  { value: 'agreeableness_collaboration', label: 'Keramahan & Kolaborasi' },
  { value: 'integrity_work_ethic', label: 'Integritas & Etika Kerja' },
];

const INITIAL_FORM = {
  dimension_a: 'extro_intro',
  statement_a: '',
  dimension_b: 'emotional_stability',
  statement_b: '',
  is_active: true,
};

export default function PsychotestManager() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await API.get('/recruiter/psychotest/questions');
      setRows(res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal memuat bank soal psikotest');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const activeCount = useMemo(() => rows.filter((r) => Number(r.is_active) === 1).length, [rows]);

  const resetForm = () => {
    setEditingId(null);
    setForm(INITIAL_FORM);
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setForm({
      dimension_a: row.dimension_a,
      statement_a: row.statement_a,
      dimension_b: row.dimension_b,
      statement_b: row.statement_b,
      is_active: Number(row.is_active) === 1,
    });
    setError('');
  };

  const save = async () => {
    if (!form.statement_a.trim() || !form.statement_b.trim()) {
      setError('Pernyataan A dan B wajib diisi');
      return;
    }
    if (form.dimension_a === form.dimension_b) {
      setError('Dimensi A dan B harus berbeda agar hasil lebih seimbang');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        is_active: form.is_active ? 1 : 0,
      };
      if (editingId) {
        await API.put(`/recruiter/psychotest/question/${editingId}`, payload);
      } else {
        await API.post('/recruiter/psychotest/question', payload);
      }
      resetForm();
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal menyimpan soal psikotest');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    const ok = window.confirm('Hapus soal psikotest ini?');
    if (!ok) return;

    try {
      await API.delete(`/recruiter/psychotest/question/${id}`);
      if (editingId === id) resetForm();
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal menghapus soal');
    }
  };

  return (
    <div className="psy-man-page">
      <header className="psy-man-header">
        <h1>Kelola Soal Psikotest</h1>
        <div className="psy-man-actions">
          <span>Aktif: {activeCount} soal</span>
          <button onClick={() => navigate('/recruiter')}>Kembali ke Recruiter</button>
        </div>
      </header>

      {error && <div className="psy-man-error">{error}</div>}

      <section className="psy-man-form">
        <h2>{editingId ? 'Edit Soal Psikotest' : 'Tambah Soal Psikotest'}</h2>
        <div className="grid-2">
          <label>
            Dimensi A
            <select
              value={form.dimension_a}
              onChange={(e) => setForm({ ...form, dimension_a: e.target.value })}
            >
              {DIMENSIONS.map((d) => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </label>
          <label>
            Dimensi B
            <select
              value={form.dimension_b}
              onChange={(e) => setForm({ ...form, dimension_b: e.target.value })}
            >
              {DIMENSIONS.map((d) => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </label>
          <label>
            Status
            <select
              value={form.is_active ? '1' : '0'}
              onChange={(e) => setForm({ ...form, is_active: e.target.value === '1' })}
            >
              <option value="1">Aktif</option>
              <option value="0">Nonaktif</option>
            </select>
          </label>
        </div>

        <label>
          Pernyataan A
          <textarea
            rows={3}
            value={form.statement_a}
            onChange={(e) => setForm({ ...form, statement_a: e.target.value })}
            placeholder="Masukkan pernyataan A"
          />
        </label>

        <label>
          Pernyataan B
          <textarea
            rows={3}
            value={form.statement_b}
            onChange={(e) => setForm({ ...form, statement_b: e.target.value })}
            placeholder="Masukkan pernyataan B"
          />
        </label>

        <div className="psy-man-btns">
          <button onClick={save} disabled={saving}>{saving ? 'Menyimpan...' : 'Simpan'}</button>
          {editingId && <button className="ghost" onClick={resetForm}>Batal Edit</button>}
        </div>
      </section>

      <section className="psy-man-table-wrap">
        <h2>Daftar Soal Psikotest</h2>
        {loading ? (
          <div>Memuat...</div>
        ) : (
          <table className="psy-man-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Dimensi A</th>
                <th>Pernyataan A</th>
                <th>Dimensi B</th>
                <th>Pernyataan B</th>
                <th>Status</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td>{DIMENSIONS.find((d) => d.value === row.dimension_a)?.label || row.dimension_a}</td>
                  <td>{row.statement_a}</td>
                  <td>{DIMENSIONS.find((d) => d.value === row.dimension_b)?.label || row.dimension_b}</td>
                  <td>{row.statement_b}</td>
                  <td>{Number(row.is_active) === 1 ? 'Aktif' : 'Nonaktif'}</td>
                  <td>
                    <button onClick={() => startEdit(row)}>Edit</button>
                    <button className="danger" onClick={() => remove(row.id)}>Delete</button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7}>Belum ada soal psikotest</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
